import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloseIcon from "@material-ui/icons/Close";
import { IconButton, Tooltip } from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import {
  DATA_TAG_DUPLICATE_FILE_DATA_2,
  DATA_TAG_DUPLICATE_USER_DATA_2,
} from "../DataTagConstants";
import "./DuplicateUserDrillDown.scss";
import { CATEGORY_LOOKUP, SENSITIVITY_LOOKUP } from "./DataTagAllFilesTable";
import { categoryIcons } from "./DataEstateDrives";
import { isEmpty } from "lodash";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";

const PAGE_SIZE = 20;

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function formatSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function fileName(path = "") {
  return path.split("/").filter(Boolean).pop() || path;
}

function SensitivityBadge({ value }) {
  const key = (value || "").toUpperCase();

  const cls = ["HIGH", "MEDIUM", "LOW", "CRITICAL"].includes(key)
    ? `drill-sensitivity--${key}`
    : "drill-sensitivity--default";

  return <span className={`drill-sensitivity ${cls}`}>{value || "—"}</span>;
}

const DRILL_COLUMNS = [
  {
    field: "username",
    headerName: "User",
    flex: 1.5,
    sortable: true,
    renderCell: ({ row }) => (
      <div className="drawer-user-cell">
        <div className="drawer-user-avatar">{getInitials(row.username)}</div>
        <span className="drawer-user-name">{row.username}</span>
      </div>
    ),
  },

  {
    field: "duplicateFile",
    headerName: "Duplicate File",
    flex: 1.6,
    sortable: true,
    renderCell: ({ value }) => (
      <span className="drill-file-name" title={value}>
        {fileName(value)}
      </span>
    ),
  },

  {
    field: "size",
    headerName: "Size",
    flex: 0.8,
    sortable: true,
    align: "center",
    headerAlign: "center",
    renderCell: ({ value }) => (
      <span className="drill-size">{formatSize(value)}</span>
    ),
  },

  {
    field: "category",
    headerName: "Category",
    flex: 1.4,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      if (isEmpty(params.value)) return "-";

      const values = params.value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);

      const visible = values.slice(0, 2);
      const remaining = values.slice(2);

      return (
        <div className="category-icon-cell">
          {visible.map((item) => {
            const cat = CATEGORY_LOOKUP[item];

            return categoryIcons[item] ? (
              <Tooltip key={item} title={cat?.label || item}>
                <img
                  className="categoryIconsDataTag"
                  src={categoryIcons[item]}
                  alt={item}
                />
              </Tooltip>
            ) : null;
          })}

          {remaining.length > 0 && (
            <Tooltip
              title={
                <div className="regulation-tooltip-content">
                  {remaining.map((item) => (
                    <div key={item}>{CATEGORY_LOOKUP[item]?.label || item}</div>
                  ))}
                </div>
              }
            >
              <span className="regulation-more">+{remaining.length}</span>
            </Tooltip>
          )}
        </div>
      );
    },
  },

  {
    field: "sensitivity",
    headerName: "Sensitivity",
    flex: 1.9,
    sortable: true,
    renderCell: (params) => {
      const info =
        SENSITIVITY_LOOKUP[params.value] || SENSITIVITY_LOOKUP.NO_SENSITIVITY;

      return <span className={`tag-chip ${info.className}`}>{info.label}</span>;
    },
  },
];

function DuplicateFileDrillDown({
  parentDriveName,
  selectedFile,
  onBack,
  onClose,
  loc,
}) {
  const dispatch = useDispatch();
  const loader = useSelector((state) => state.dataTagReducer.popupLoader);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id,
  );

  const duplicateFileData2 = useSelector(
    (state) => state.dataTagReducer.duplicateFileData2 || {},
  );

  const rows = duplicateFileData2?.duplicates || [];
  const totalRows = duplicateFileData2?.totalElements || 0;

  const NoRowsOverlay = () => (
    <div className="drill-empty">
      <span className="drill-empty-text">No data available</span>
    </div>
  );

  useEffect(() => {
    if (!dataSourceId || !selectedFile?.id) return;
    const fileId =
      loc === "filesTable" ? selectedFile?.fileId : selectedFile?.id;

    dispatch({
      type: DATA_TAG_DUPLICATE_FILE_DATA_2,
      payload: {
        dataSourceId,
        fileId,
        page: paginationModel.page,
        size: paginationModel.pageSize,
      },
    });
  }, [
    dataSourceId,
    selectedFile?.id,
    paginationModel.page,
    paginationModel.pageSize,
    dispatch,
  ]);

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: PAGE_SIZE,
    });
  }, [selectedFile?.id]);

  const gridRows = rows.map((item, index) => ({
    id: item.duplicateFileId || `row-${index}`,
    username: item.duplicateActor?.username || "—",
    email: item.duplicateActor?.email || "—",
    duplicateFile: item.duplicateFile || "—",
    size: item.size || 0,
    category: item.category,
    sensitivity: item.sensitivity,
  }));

  return (
    <>
      {loader && loc !== "filesTable" && (
        <img
          src={Page_Loader}
          alt="loading"
          className="loader-data-source dataTagPopupLoader"
        ></img>
      )}
      {loc !== "filesTable" && (
        <div className="drill-header">
          <div className="drill-header-top-row">
            <div className="drill-breadcrumb">
              <button className="drill-back-btn" onClick={onBack}>
                <ArrowBackIcon style={{ fontSize: 22 }} />
                {parentDriveName}
              </button>

              <NavigateNextIcon
                className="drill-breadcrumb-sep"
                style={{ fontSize: 22 }}
              />

              <span className="drill-breadcrumb-current">
                {selectedFile?.fileName ||
                  selectedFile?.duplicateFile ||
                  "File"}
              </span>
            </div>

            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close drawer"
              className="drill-close-btn"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}
      <div
        className={`drill-body ${loc === "filesTable" ? "files-table" : ""}`}
      >
        <div className="drill-grid-wrap">
          <DataGrid
            rows={gridRows}
            columns={DRILL_COLUMNS}
            pagination
            paginationMode="server"
            rowCount={totalRows}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[20, 50, 100]}
            disableRowSelectionOnClick
            disableColumnMenu
            localeText={{
              noRowsLabel: "No data available",
            }}
          />
        </div>
      </div>
    </>
  );
}

export default DuplicateFileDrillDown;
